import { useMemo, useState, type ReactNode } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Paper, Stack, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { Navigate, useSearchParams } from "react-router-dom";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchActivity, fetchInboxBacklog, fetchManualEntry, fetchMessages, type ActivityReport, type ActivityRow,
  type InboxBacklogReport, type MessageResolution, type MessageRoute, type MessagesDay, type MessagesReport,
} from "@/api/activity";

const fmtPct = (n: number | null | undefined) => (n == null ? "-" : `${n}%`);
const fmtMin = (n: number | null | undefined) => {
  if (n == null) return "-";
  if (n < 60) return `${Math.round(n)} min`;
  if (n < 60 * 24) return `${(n / 60).toFixed(1)} h`;
  return `${(n / 60 / 24).toFixed(1)} d`;
};

function userLabel(report: ActivityReport, id: number | null): string {
  if (id == null) return "(unattributed)";
  const u = report.users[String(id)];
  return u ? (u.name || u.email) : `User ${id}`;
}

// CSV of the per-day per-user table, quoted so names with commas survive.
function toCsv(report: ActivityReport): string {
  const cols: Array<[string, (r: ActivityRow) => unknown]> = [
    ["date", (r) => r.date],
    ["user", (r) => userLabel(report, r.user_id)],
    ["email", (r) => (r.user_id != null ? report.users[String(r.user_id)]?.email ?? "" : "")],
    ["bills_posted", (r) => r.bills_posted],
    ["credits_posted", (r) => r.credits_posted],
    ["lines_posted", (r) => r.lines_posted],
    ["fields_edited", (r) => r.fields_edited],
    ["lines_added", (r) => r.lines_added],
    ["lines_removed", (r) => r.lines_removed],
    ["lines_changed", (r) => r.lines_changed],
    ["pct_auto_filled_kept", (r) => r.pct_auto_filled_kept ?? ""],
    ["median_minutes_to_post", (r) => r.median_minutes_to_post ?? ""],
    ["duplicate_overrides", (r) => r.duplicate_overrides],
    ["estimated", (r) => (r.estimated ? "yes" : "no")],
  ];
  const quote = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map(([h]) => h).join(","), ...report.rows.map((r) => cols.map(([, f]) => quote(f(r))).join(","))].join("\n");
}

function downloadCsv(report: ActivityReport) {
  const blob = new Blob([toCsv(report)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ap-activity-${report.from}-to-${report.to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Every date in the range, so quiet days show as gaps instead of vanishing.
function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = dayjs(from); !d.isAfter(dayjs(to)); d = d.add(1, "day")) out.push(d.format("YYYY-MM-DD"));
  return out;
}

// Minimal bar chart in plain SVG (the SPA has no chart library; not worth a
// dependency for a handful of charts). One bar per bucket (day or ISO week),
// value labels on hover. A null value is a gap, e.g. an average with no bills.
function BucketBars({ title, buckets, values, format = (n) => String(n), color = "primary.main" }: {
  title: string; buckets: Bucket[]; values: Array<number | null>; format?: (n: number) => string; color?: string;
}) {
  const max = Math.max(1, ...values.map((v) => v ?? 0));
  const h = 120;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: h, borderBottom: 1, borderColor: "divider" }}>
        {buckets.map((b, i) => {
          const v = values[i];
          return (
            <Tooltip key={b.key} title={`${b.label}: ${v == null ? "-" : format(v)}`}>
              <Box sx={{
                flex: 1, minWidth: 2, height: v ? `${(v / max) * 100}%` : 0, bgcolor: color, borderRadius: "2px 2px 0 0",
                opacity: b.partial ? 0.6 : 1,
              }} />
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">{buckets[0]?.label ?? ""}</Typography>
        <Typography variant="caption" color="text.secondary">max {format(max)}</Typography>
        <Typography variant="caption" color="text.secondary">{buckets[buckets.length - 1]?.label ?? ""}</Typography>
      </Stack>
    </Paper>
  );
}

// Same plain SVG/MUI bars as BucketBars, with several series: stacked into one
// column per bucket (or side by side, to compare two flows), a legend, a
// per-bucket tooltip, and an optional percentage line on a 0-100% right axis.
type StackSeries = { key: string; label: string; color: string; values: number[] };
type Grouping = "day" | "week";

export interface Bucket { key: string; label: string; partial: boolean; days: string[] }

// Monday of the ISO week a YYYY-MM-DD date falls in (dates are already Central).
export function isoWeekStart(date: string): string {
  const d = dayjs(date);
  return d.subtract((d.day() + 6) % 7, "day").format("YYYY-MM-DD");
}

// Days grouped into day or ISO-week buckets. A week the selected range only
// partly covers (typically the current one) is marked partial.
export function bucketize(days: string[], grouping: Grouping): Bucket[] {
  if (grouping === "day") {
    return days.map((d) => ({ key: d, label: dayjs(d).format("ddd MMM D"), partial: false, days: [d] }));
  }
  const weeks = new Map<string, string[]>();
  days.forEach((d) => {
    const w = isoWeekStart(d);
    weeks.set(w, [...(weeks.get(w) ?? []), d]);
  });
  return Array.from(weeks.entries()).map(([w, ds]) => ({
    key: w, days: ds, partial: ds.length < 7,
    label: `Week of ${dayjs(w).format("MMM D")}${ds.length < 7 ? " (partial)" : ""}`,
  }));
}

const PCT_COLOR = "#2e7d32";

function StackedBars({ buckets, series, pct, pctLabel, sideBySide = false, labelEach = false }: {
  buckets: Bucket[]; series: StackSeries[]; pct?: Array<number | null>; pctLabel?: string; sideBySide?: boolean;
  // Name every bar (e.g. one group per person) instead of only the first and last.
  labelEach?: boolean;
}) {
  const totals = buckets.map((_, i) => series.reduce((s, x) => s + (x.values[i] ?? 0), 0));
  const max = Math.max(1, ...(sideBySide ? series.flatMap((x) => x.values) : totals));
  const n = buckets.length;
  const points = (pct ?? []).map((v, i) => (v == null ? null : { x: ((i + 0.5) / n) * 100, y: 100 - v }));
  const valid = points.filter((p): p is { x: number; y: number } => p != null);
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: "wrap" }}>
        {series.map((x) => (
          <Stack key={x.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 12, bgcolor: x.color, borderRadius: 0.5 }} />
            <Typography variant="caption">{x.label}</Typography>
          </Stack>
        ))}
        {pct && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 14, height: 3, bgcolor: PCT_COLOR }} />
            <Typography variant="caption">{pctLabel} (right axis)</Typography>
          </Stack>
        )}
      </Stack>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 160, borderBottom: 1, borderColor: "divider" }}>
            {buckets.map((b, i) => (
              <Tooltip key={b.key} title={
                <Box>
                  <div>{b.label}</div>
                  {series.map((x) => <div key={x.key}>{x.label}: {x.values[i] ?? 0}</div>)}
                  {!sideBySide && series.length > 1 && <div><strong>Total: {totals[i]}</strong></div>}
                  {pct && <div>{pctLabel}: {pct[i] == null ? "-" : `${pct[i]}%`}</div>}
                </Box>
              }>
                {sideBySide ? (
                  <Box sx={{ flex: 1, minWidth: 2, height: "100%", display: "flex", alignItems: "flex-end", gap: "1px",
                    opacity: b.partial ? 0.6 : 1 }}>
                    {series.map((x) => (
                      <Box key={x.key} sx={{ flex: 1, height: `${((x.values[i] ?? 0) / max) * 100}%`, bgcolor: x.color }} />
                    ))}
                  </Box>
                ) : (
                  <Box sx={{
                    flex: 1, minWidth: 2, height: `${(totals[i] / max) * 100}%`, display: "flex",
                    flexDirection: "column-reverse", opacity: b.partial ? 0.6 : 1,
                  }}>
                    {series.map((x) => (
                      <Box key={x.key} sx={{ height: totals[i] ? `${((x.values[i] ?? 0) / totals[i]) * 100}%` : 0, bgcolor: x.color }} />
                    ))}
                  </Box>
                )}
              </Tooltip>
            ))}
          </Box>
          {valid.length > 0 && (
            <Box sx={{ position: "absolute", inset: 0, bottom: 1, height: 160, pointerEvents: "none" }}>
              <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <polyline points={valid.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke={PCT_COLOR}
                  strokeWidth={2} vectorEffect="non-scaling-stroke" />
              </svg>
              {valid.map((p) => (
                <Box key={p.x} sx={{
                  position: "absolute", left: `${p.x}%`, top: `${p.y}%`, width: 7, height: 7, ml: "-3.5px", mt: "-3.5px",
                  borderRadius: "50%", bgcolor: PCT_COLOR, border: "1px solid white",
                }} />
              ))}
            </Box>
          )}
        </Box>
        {pct && (
          <Box sx={{ width: 36, height: 160, display: "flex", flexDirection: "column", justifyContent: "space-between", pl: 0.5 }}>
            {["100%", "50%", "0%"].map((l) => <Typography key={l} variant="caption" color="text.secondary">{l}</Typography>)}
          </Box>
        )}
      </Box>
      {labelEach ? (
        <Box sx={{ display: "flex", gap: "2px", pr: pct ? 4.5 : 0 }}>
          {buckets.map((b) => (
            <Typography key={b.key} variant="caption" color="text.secondary" noWrap
              sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>{b.label}</Typography>
          ))}
        </Box>
      ) : (
        <Stack direction="row" justifyContent="space-between" sx={{ pr: pct ? 4.5 : 0 }}>
          <Typography variant="caption" color="text.secondary">{buckets[0]?.label ?? ""}</Typography>
          <Typography variant="caption" color="text.secondary">max {max} per bucket</Typography>
          <Typography variant="caption" color="text.secondary">{buckets[n - 1]?.label ?? ""}</Typography>
        </Stack>
      )}
    </Box>
  );
}

const pctThroughApp = (app: number, inbox: number) => (app + inbox > 0 ? Math.round((100 * app) / (app + inbox)) : null);

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 140, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5">{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
}

// Rounded dollars for the backlog estimate: $612, $84K, $1.29M.
const fmtUsd = (n: number | null | undefined) => {
  if (n == null) return "-";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};
const fmtDays = (n: number | null) => (n == null ? "Not shrinking" : `${n} days`);

// Always on, whatever dates are picked: what is waiting in Needs review and
// Ready to bill, the pace against it, and the dollars likely in it.
function InboxBacklogPanel({ b }: { b: InboxBacklogReport }) {
  const { backlog, pace, bill_rate: rate, dollars } = b;
  const dup = dollars.possible_duplicates;
  const [showNotes, setShowNotes] = useState(false);
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>Inbox backlog (Needs review + Ready to bill)</Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1, mb: 1 }}>
        <Stat label="In the queues now" value={backlog.total}
          hint={`${backlog.needs_review} Needs review, ${backlog.ready_to_bill} Ready to bill; ${backlog.invoices} invoices`} />
        <Stat label="Days to clear (30-day pace)" value={fmtDays(pace.days_to_clear)}
          hint={`Last week's pace: ${fmtDays(pace.days_to_clear_last_week)}. With nothing new: ${pace.days_to_clear_no_arrivals ?? "-"} days`} />
        <Stat label="Finished per day" value={pace.finished_per_day}
          hint={`Last week: ${pace.last_week_finished_per_day} per day`} />
        <Stat label="New per day" value={pace.arrivals_per_day}
          hint={`Into the queues; ${pace.emails_per_day} emails per day in all`} />
        <Stat label="Contain a bill" value={fmtPct(rate.overall.pct)}
          hint={`Ready to bill ${fmtPct(rate.ready_to_bill.pct)}, Needs review ${fmtPct(rate.needs_review.pct)}`} />
        <Stat label="$ in the queues (midpoint)" value={fmtUsd(dollars.p50)}
          hint={`80% likely ${fmtUsd(dollars.p10)} to ${fmtUsd(dollars.p90)}; 90% likely ${fmtUsd(dollars.p5)} to ${fmtUsd(dollars.p95)}`} />
      </Stack>
      <Button size="small" sx={{ textTransform: "none", px: 0 }} onClick={() => setShowNotes((v) => !v)}>
        {showNotes ? "Hide how these are counted" : "How these are counted"}
      </Button>
      <Collapse in={showNotes}>
      <Typography variant="body2" color="text.secondary">
        Per day means per business day over the last {pace.window_days} days ({pace.business_days} business days);
        last week is the week of {dayjs(pace.last_week_start).format("MMM D")}. Only items the agent put in a
        person's queue count as new or finished. The median item has waited {backlog.median_age_days ?? "-"} days,
        the oldest {backlog.oldest_age_days ?? "-"}. "Contain a bill" is the share of items people finished in the last
        {" "}{rate.window_days} days that were billed or marked payable.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Dollars: {fmtUsd(dollars.known_total)} is read from invoice totals on {dollars.known_items} item(s)
        {dollars.largest_known ? ` (largest single invoice ${fmtUsd(dollars.largest_known)})` : ""}.
        {dollars.unknown_valued
          ? ` The other ${dollars.unknown_invoices} invoice(s) are estimated from the ${dollars.history_bills} bills posted in the last ${rate.window_days} days (median ${fmtUsd(dollars.history_median)}).`
          : ` The other ${dollars.unknown_invoices} invoice(s) have no total and no posted history to estimate from, so they are left out.`}
        {" "}Each counts at its queue's bill rate.
        {dup.amounts > 0 && ` Possible duplicates: ${dup.amounts} amount(s) appear on more than one item, ${fmtUsd(dup.extra_total)} beyond the first copy; check before billing.`}
      </Typography>
      </Collapse>
    </Paper>
  );
}

const ROUTE_SERIES: Array<{ key: MessageRoute; label: string; color: string }> = [
  { key: "ready_to_bill", label: "Ready to bill", color: "#1976d2" },
  { key: "needs_review", label: "Needs review", color: "#ed6c02" },
  { key: "quarantined", label: "Quarantined as spam", color: "#9e9e9e" },
  { key: "no_action", label: "Filed by the agent (no action)", color: "#cfd8dc" },
  { key: "not_triaged", label: "Not triaged yet", color: "#7e57c2" },
];
const RESOLUTION_SERIES: Array<{ key: MessageResolution; label: string; color: string }> = [
  { key: "billed", label: "Billed in FP Accounting", color: "#1976d2" },
  { key: "marked_payable", label: "Marked payable, no bill here", color: "#ed6c02" },
  { key: "not_a_bill", label: "Dismissed by a person (not a bill)", color: "#795548" },
  { key: "quarantined", label: "Quarantined as spam", color: "#9e9e9e" },
  { key: "filed", label: "Filed by the agent (no action)", color: "#cfd8dc" },
];

type TabKey = "inbox" | "bills" | "people";
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "inbox", label: "Inbox" },
  { key: "bills", label: "Bills" },
  { key: "people", label: "People & vendors" },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      {children}
    </Paper>
  );
}

// The inbox counted by email: what came in vs what was resolved, the backlog
// at the end of each bucket and when it would reach zero, then the breakdowns.
function InboxTab({ backlog, messages, buckets }: {
  backlog: UseQueryResult<InboxBacklogReport>;
  messages: UseQueryResult<MessagesReport>;
  buckets: Bucket[];
}) {
  const byDay = useMemo(() => new Map((messages.data?.by_day ?? []).map((d) => [d.date, d])), [messages.data]);
  const sum = (f: (d: MessagesDay) => number) =>
    buckets.map((b) => b.days.reduce((acc, d) => { const m = byDay.get(d); return acc + (m ? f(m) : 0); }, 0));
  const total = (r: Record<string, number>) => Object.values(r).reduce((a, n) => a + n, 0);
  const routeSeries = ROUTE_SERIES.map((x) => ({ ...x, values: sum((d) => d.received[x.key]) }));
  const resolutionSeries = RESOLUTION_SERIES.map((x) => ({ ...x, values: sum((d) => d.resolved[x.key]) }));
  const cameIn = sum((d) => total(d.received));
  const resolved = sum((d) => total(d.resolved));
  // The backlog on the last day of each bucket.
  const backlogValues = buckets.map((b) => byDay.get(b.days[b.days.length - 1])?.backlog ?? null);
  const billed = resolutionSeries[0].values;
  const payable = resolutionSeries[1].values;
  const billedPct = buckets.map((_, i) => pctThroughApp(billed[i], payable[i]));
  const t = messages.data?.totals;
  const pace = messages.data?.pace;
  const parts = <K extends string>(r: Record<K, number>, series: Array<{ key: K; label: string }>) =>
    series.map((x) => `${r[x.key]} ${x.label}`).join(", ");

  return (
    <Stack spacing={2}>
      {messages.isLoading && <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}><CircularProgress /></Box>}
      {messages.isError && <Alert severity="error">Couldn't load the inbox counts.</Alert>}
      {t && pace && (
        <>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Stat label="Came in" value={t.received_total} hint={parts(t.received, ROUTE_SERIES)} />
            <Stat label="Resolved" value={t.resolved_total} hint={parts(t.resolved, RESOLUTION_SERIES)} />
            <Stat label="Backlog" value={t.backlog_end ?? "-"}
              hint={`Needs review + Ready to bill at the end of the range; ${t.backlog_start ?? "-"} at the start`} />
            <Stat label="Reaches zero" value={pace.clears_on ? dayjs(pace.clears_on).format("MMM D, YYYY") : "Not shrinking"}
              hint={`At the last ${pace.window_days} days' pace: ${pace.into_per_day} into the queues and ${pace.out_per_day} out per day`
                + (pace.days_to_zero ? ` (${pace.days_to_zero} days from today).` : ".")} />
          </Stack>
          <Section title="Came in vs resolved">
            <StackedBars buckets={buckets} sideBySide series={[
              { key: "in", label: "Came in", color: "#ed6c02", values: cameIn },
              { key: "resolved", label: "Resolved", color: "#1976d2", values: resolved },
            ]} />
            <Typography variant="body2" color="text.secondary">
              Resolved means the email needs nothing more: the agent quarantined it as spam or filed it with no
              action when it arrived, or a person finished it (billed, marked payable or dismissed).
            </Typography>
          </Section>
          <BucketBars title="Backlog (Needs review + Ready to bill) at the end of each period" buckets={buckets}
            values={backlogValues} color="#ed6c02" />
          <Section title="Came in, by where the agent sent it">
            <StackedBars buckets={buckets} series={routeSeries} />
          </Section>
          <Section title="Resolved, by how">
            <StackedBars buckets={buckets} series={resolutionSeries} pct={billedPct}
              pctLabel="% of emails with a bill handled in FP Accounting" />
            <Typography variant="body2" color="text.secondary">
              The agent's calls count on the day the email arrived; a person's on the day they finished it. Billed
              means at least one of its invoices posted through FP Accounting; marked payable means the reviewer sent
              it on without posting a bill here.
            </Typography>
          </Section>
        </>
      )}

      {backlog.isLoading && <CircularProgress size={20} />}
      {backlog.isError && <Alert severity="warning">Couldn't load the inbox backlog. The rest of the report is unaffected.</Alert>}
      {backlog.data && <InboxBacklogPanel b={backlog.data} />}
    </Stack>
  );
}

// Inbox work per reviewer: threads they first opened vs items they resolved.
// Emails aren't assigned, so "opened" stands in for what each person picked up.
function InboxByPerson({ report }: { report: MessagesReport }) {
  const people = report.by_user;
  const baseName = (id: number) => {
    const u = report.users[String(id)];
    return u ? (u.name || u.email) : `User ${id}`;
  };
  // Two accounts with the same name (e.g. old and new email domains) get their email.
  const name = (id: number) => {
    const n = baseName(id);
    const clash = people.some((p) => p.user_id !== id && baseName(p.user_id) === n);
    return clash ? `${n} (${report.users[String(id)]?.email ?? id})` : n;
  };
  // One bar group per person, reusing the bucket chart.
  const buckets: Bucket[] = people.map((p) => ({ key: String(p.user_id), label: name(p.user_id), partial: false, days: [] }));
  return (
    <Section title="Inbox by person: opened vs resolved">
      {people.length === 0 ? (
        <Typography color="text.secondary">No inbox activity in this range.</Typography>
      ) : (
        <>
          <StackedBars buckets={buckets} sideBySide labelEach series={[
            { key: "opened", label: "Opened (first time)", color: "#ed6c02", values: people.map((p) => p.opened) },
            { key: "resolved", label: "Resolved", color: "#1976d2", values: people.map((p) => p.resolved_total) },
          ]} />
          <Table size="small" sx={{ maxWidth: 760 }}>
            <TableHead>
              <TableRow>
                <TableCell>Person</TableCell>
                <TableCell align="right">Opened</TableCell>
                <TableCell align="right">Resolved</TableCell>
                <TableCell align="right">Billed here</TableCell>
                <TableCell align="right">Marked payable</TableCell>
                <TableCell align="right">Dismissed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {people.map((p) => (
                <TableRow key={p.user_id}>
                  <TableCell>{name(p.user_id)}</TableCell>
                  <TableCell align="right">{p.opened}</TableCell>
                  <TableCell align="right">{p.resolved_total}</TableCell>
                  <TableCell align="right">{p.resolved.billed}</TableCell>
                  <TableCell align="right">{p.resolved.marked_payable}</TableCell>
                  <TableCell align="right">{p.resolved.not_a_bill}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Emails aren't assigned to anyone, so this shows what each person opened rather than what came in for them.
            Opened counts email threads a person opened (or marked read) for the first time in the range. Resolved counts
            Needs review and Ready to bill items they finished; the agent's own quarantines and no-action calls belong
            to no one.
          </Typography>
        </>
      )}
    </Section>
  );
}

export function ActivityPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as TabKey | null;
  const tab: TabKey = tabParam && TABS.some((x) => x.key === tabParam) ? tabParam : "inbox";
  const setTab = (v: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (v === "inbox") next.delete("tab");
    else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  // Week view by default: the last 12 ISO weeks (this week included, partial).
  const twelveWeeksFrom = () => dayjs(isoWeekStart(dayjs().format("YYYY-MM-DD"))).subtract(11, "week").format("YYYY-MM-DD");
  const [grouping, setGrouping] = useState<Grouping>("week");
  const [from, setFrom] = useState(twelveWeeksFrom);
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  // Switching Day/Week resets the range to that view's default until the
  // reviewer picks dates themselves.
  const [rangeTouched, setRangeTouched] = useState(false);
  const changeGrouping = (g: Grouping) => {
    setGrouping(g);
    if (rangeTouched) return;
    setFrom(g === "week" ? twelveWeeksFrom() : dayjs().subtract(29, "day").format("YYYY-MM-DD"));
    setTo(dayjs().format("YYYY-MM-DD"));
  };

  const enabled = !!user?.super_admin;
  const report = useQuery({ queryKey: ["admin_activity", from, to], queryFn: () => fetchActivity(from, to), enabled });
  const manual = useQuery({
    queryKey: ["admin_activity_manual", from, to], queryFn: () => fetchManualEntry(from, to), enabled, retry: false,
  });
  const backlog = useQuery({ queryKey: ["admin_activity_backlog"], queryFn: fetchInboxBacklog, enabled });
  const messages = useQuery({
    queryKey: ["admin_activity_messages", from, to], queryFn: () => fetchMessages(from, to), enabled,
  });

  const data = report.data;
  // The server's normalized range (swapped, clamped, defaulted), never the raw
  // inputs: an empty date would make daysBetween loop forever.
  const range = data ?? messages.data;
  const days = useMemo(() => (range ? daysBetween(range.from, range.to) : []), [range]);
  const perDay = useMemo(() => {
    const m = new Map<string, { posted: number; fields: number }>();
    (data?.rows ?? []).forEach((r) => {
      const cur = m.get(r.date) ?? { posted: 0, fields: 0 };
      cur.posted += r.bills_posted + r.credits_posted;
      cur.fields += r.fields_edited;
      m.set(r.date, cur);
    });
    return m;
  }, [data]);

  // Super-admins only; the API enforces this too.
  if (user && !user.super_admin) return <Navigate to="/" replace />;

  const t = data?.totals;
  const posted = t ? t.bills_posted + t.credits_posted : 0;
  const md = manual.data;
  const fromInbox = md?.total_from_inbox ?? 0;
  const inboxPct = md ? pctThroughApp(posted, fromInbox) : null;
  const manualByDay = new Map((md?.by_day ?? []).map((d) => [d.date, d]));
  const buckets = bucketize(days, grouping);
  const sumDays = (b: Bucket, f: (d: string) => number) => b.days.reduce((acc, d) => acc + f(d), 0);
  const appValues = buckets.map((b) => sumDays(b, (d) => perDay.get(d)?.posted ?? 0));
  const fieldsValues = buckets.map((b) => sumDays(b, (d) => perDay.get(d)?.fields ?? 0));
  const fieldsPerBill = buckets.map((_, i) => (appValues[i] ? Math.round((fieldsValues[i] / appValues[i]) * 10) / 10 : null));
  const inboxValues = buckets.map((b) => sumDays(b, (d) => manualByDay.get(d)?.from_inbox ?? 0));
  const otherValues = buckets.map((b) => sumDays(b, (d) => manualByDay.get(d)?.other_manual ?? 0));
  const appSeries: StackSeries = { key: "app", label: "FP Accounting", color: "#1976d2", values: appValues };
  const entrySeries: StackSeries[] = md
    ? [
        appSeries,
        { key: "inbox", label: "From the inbox (manual in NetSuite)", color: "#ed6c02", values: inboxValues },
        { key: "other", label: "Other manual (no inbox document)", color: "#9e9e9e", values: otherValues },
      ]
    : [appSeries];
  // Skipped (null) for buckets with neither AP triage nor inbox bills.
  const inboxPctLine = md ? buckets.map((_, i) => pctThroughApp(appValues[i], inboxValues[i])) : undefined;

  const billsLoading = (
    <>
      {report.isLoading && <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>}
      {report.isError && <Alert severity="error">Couldn't load the activity report.</Alert>}
    </>
  );

  return (
    <AppLayout maxWidth="xl">
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Activity</Typography>
        <TextField label="From" type="date" size="small" value={from} onChange={(e) => { setRangeTouched(true); setFrom(e.target.value); }}
          InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" size="small" value={to} onChange={(e) => { setRangeTouched(true); setTo(e.target.value); }}
          InputLabelProps={{ shrink: true }} />
        <ToggleButtonGroup size="small" exclusive value={grouping} onChange={(_, v) => v && changeGrouping(v)}>
          <ToggleButton value="day" sx={{ textTransform: "none", px: 1.5 }}>Day</ToggleButton>
          <ToggleButton value="week" sx={{ textTransform: "none", px: 1.5 }}>Week</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!data} onClick={() => data && downloadCsv(data)}>
          CSV
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Private to super admins. Days are in {data?.time_zone ?? "Central time"}. The dates and Day/Week apply to
        every chart except the inbox backlog, which always looks at the last 30 and 90 days.
      </Typography>

      <Tabs value={tab} onChange={(_e, v: TabKey) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        {TABS.map((x) => <Tab key={x.key} value={x.key} label={x.label} sx={{ textTransform: "none" }} />)}
      </Tabs>

      {tab === "inbox" && <InboxTab backlog={backlog} messages={messages} buckets={buckets} />}

      {tab === "bills" && billsLoading}
      {tab === "bills" && data && t && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Stat label="Bills posted" value={t.bills_posted} />
            <Stat label="Credits posted" value={t.credits_posted} />
            <Stat label="Lines posted" value={t.lines_posted} />
            <Stat label="Fields edited" value={t.fields_edited} hint={posted ? `${(t.fields_edited / posted).toFixed(1)} per bill` : undefined} />
            <Stat label="Auto-filled fields kept" value={fmtPct(t.pct_auto_filled_kept)} />
            <Stat label="Median ready → posted" value={fmtMin(t.median_minutes_to_post)} />
            <Stat label="Duplicate overrides" value={t.duplicate_overrides} />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <BucketBars title="Bills + credits posted" buckets={buckets} values={appValues} />
            <BucketBars title="Manual fields per bill" buckets={buckets} color="warning.main" values={fieldsPerBill} />
          </Stack>

          <Section title="Entered through FP Accounting vs keyed in NetSuite">
            {md && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{inboxPct == null ? "-" : `${inboxPct}%`}</strong> of inbox invoices posted through FP Accounting
                {" "}({posted} through FP Accounting, {fromInbox} keyed in NetSuite from the inbox).
              </Typography>
            )}
            <StackedBars buckets={buckets} series={entrySeries} pct={inboxPctLine}
              pctLabel="% of inbox invoices via FP Accounting" />
            {manual.isLoading && <CircularProgress size={20} />}
            {manual.isError && (
              <Alert severity="info">NetSuite couldn't be reached for the manual-entry count. The rest of the report is unaffected.</Alert>
            )}
            {md && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Also keyed in NetSuite: {md.total_other_manual} bill(s) with no inbox document (rent, utilities,
                  benefits, taxes, portal bills). Not counted above: {md.total_direct_pay} automated Direct Pay
                  bill(s) (CED - Direct Pay).
                </Typography>
                <Table size="small" sx={{ maxWidth: 640 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Created by (NetSuite)</TableCell>
                      <TableCell align="right">From the inbox</TableCell>
                      <TableCell align="right">Other manual</TableCell>
                      <TableCell align="right">Bills</TableCell>
                      <TableCell align="right">Credits</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {md.creators.map((c) => (
                      <TableRow key={c.creator} sx={c.automated ? { "& td": { color: "text.secondary" } } : undefined}>
                        <TableCell>{c.creator}</TableCell>
                        <TableCell align="right">{c.from_inbox}</TableCell>
                        <TableCell align="right">{c.other_manual}</TableCell>
                        <TableCell align="right">{c.bills}</TableCell>
                        <TableCell align="right">{c.credits}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Section>
        </Stack>
      )}

      {tab === "people" && (
        <Box sx={{ mb: 2 }}>
          {messages.isError && <Alert severity="error">Couldn't load the inbox counts.</Alert>}
          {messages.data && <InboxByPerson report={messages.data} />}
        </Box>
      )}
      {tab === "people" && billsLoading}
      {tab === "people" && data && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary" component="div">
            Rows marked <Chip size="small" label="estimated" sx={{ height: 18 }} /> cover bills from before edit
            tracking began; their edits are estimated by comparing Claude's raw read with what posted, which
            overcounts coding the vendor rules filled in.
          </Typography>
          <Paper variant="outlined" sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell><TableCell>User</TableCell>
                  <TableCell align="right">Bills</TableCell><TableCell align="right">Credits</TableCell>
                  <TableCell align="right">Lines</TableCell><TableCell align="right">Fields edited</TableCell>
                  <TableCell align="right">Lines added</TableCell><TableCell align="right">Lines changed</TableCell>
                  <TableCell align="right">Auto-filled kept</TableCell><TableCell align="right">Median ready → posted</TableCell>
                  <TableCell align="right">Dup. overrides</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {[...data.rows].reverse().map((r) => (
                  <TableRow key={`${r.date}-${r.user_id}`}>
                    <TableCell>{dayjs(r.date).format("ddd MMM D")}</TableCell>
                    <TableCell>{userLabel(data, r.user_id)}</TableCell>
                    <TableCell align="right">{r.bills_posted}</TableCell>
                    <TableCell align="right">{r.credits_posted}</TableCell>
                    <TableCell align="right">{r.lines_posted}</TableCell>
                    <TableCell align="right">{r.fields_edited}</TableCell>
                    <TableCell align="right">{r.lines_added}</TableCell>
                    <TableCell align="right">{r.lines_changed}</TableCell>
                    <TableCell align="right">{fmtPct(r.pct_auto_filled_kept)}</TableCell>
                    <TableCell align="right">{fmtMin(r.median_minutes_to_post)}</TableCell>
                    <TableCell align="right">{r.duplicate_overrides}</TableCell>
                    <TableCell>{r.estimated && <Chip size="small" label="estimated" />}</TableCell>
                  </TableRow>
                ))}
                {data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={12}><Typography color="text.secondary">No activity in this range.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>Vendors that need the most manual entry</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell><TableCell align="right">Bills</TableCell>
                  <TableCell align="right">Fields changed</TableCell><TableCell align="right">Per bill</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.by_vendor.map((v) => (
                  <TableRow key={String(v.netsuite_vendor_id)}>
                    <TableCell>{v.vendor_name ?? (v.netsuite_vendor_id ? `Vendor ${v.netsuite_vendor_id}` : "(no vendor)")}</TableCell>
                    <TableCell align="right">{v.bills}</TableCell>
                    <TableCell align="right">{v.fields_changed}</TableCell>
                    <TableCell align="right">{v.fields_changed_per_bill}</TableCell>
                    <TableCell>{v.estimated && <Chip size="small" label="estimated" />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      )}
    </AppLayout>
  );
}
